"use client";

import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { errorReportRows, formatErrorReportsText } from "@/lib/errors/format";
import type { ErrorReport, ErrorReportEnvironment } from "@/lib/errors/report";

function currentUserAgent(): string | null {
  return typeof navigator === "undefined" ? null : navigator.userAgent;
}

/**
 * テキストをクリップボードへコピーする（http の環境では選択範囲のコピーに切り替える）。
 *
 * `navigator.clipboard` はセキュアコンテキスト限定で、http で配信する dev 環境では使えない。
 * そのときは一時的な textarea を `container`（ダイアログの中＝フォーカスの閉じ込めの内側）に置いて
 * `execCommand("copy")` する。
 */
async function copyText(text: string, container: HTMLElement | null): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 権限が無いなどは下の方法で試す
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  (container ?? document.body).appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

/** 1 件の全項目（展開なし・等幅・折り返し）。 */
function ErrorReportItem({
  report,
  env,
  heading,
}: {
  report: ErrorReport;
  env: ErrorReportEnvironment;
  heading: string | null;
}) {
  const rows = errorReportRows(report, env, currentUserAgent());
  return (
    <section
      className="rounded-md border border-destructive/30 bg-destructive/5 p-3"
      data-testid="error-report-item"
    >
      {heading && <h3 className="mb-2 text-xs font-medium text-destructive">{heading}</h3>}
      <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd
              className={
                row.block
                  ? "font-mono text-[11px] leading-snug whitespace-pre-wrap break-all"
                  : "font-mono whitespace-pre-wrap break-all"
              }
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * エラーの全件と「すべてコピー」（エラーダイアログと、全体のエラー画面で使う）。
 *
 * @remarks
 * 展開などの操作をしなくても全項目が見える（スクリーンショット 1 枚で原因を調べられるようにする・#456）。
 * スタックは 1 枚に収まるよう整形して出し、全文は「すべてコピー」に入れる。
 *
 * @param props.reports - 出すエラー（新しい順）。
 * @param props.env - 利用者と版。
 * @param props.actions - 「すべてコピー」の横に並べるボタン（「閉じる」など）。
 */
export function ErrorReportList({
  reports,
  env,
  actions,
}: {
  reports: readonly ErrorReport[];
  env: ErrorReportEnvironment;
  actions?: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");

  const handleCopy = async () => {
    const ok = await copyText(
      formatErrorReportsText(reports, env, currentUserAgent()),
      containerRef.current,
    );
    setCopyState(ok ? "done" : "failed");
  };

  return (
    <div ref={containerRef} className="flex min-h-0 min-w-0 flex-col gap-3">
      <div className="min-h-0 space-y-3 overflow-y-auto">
        {reports.map((report, index) => (
          <ErrorReportItem
            key={report.id}
            report={report}
            env={env}
            heading={reports.length > 1 ? `${index + 1} 件目` : null}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {copyState !== "idle" && (
          <span className="text-xs text-muted-foreground" role="status">
            {copyState === "done"
              ? "コピーしました"
              : "コピーできませんでした。画面の文字を選択してコピーしてください"}
          </span>
        )}
        <Button type="button" variant="outline" onClick={() => void handleCopy()}>
          すべてコピー
        </Button>
        {actions}
      </div>
    </div>
  );
}
